/**
 * body 接手滚动后晚这么久才允许手动滚。撤掉根页面滚动和建立 body 滚动层挤在同一帧时，
 * iOS 会把整页画成空白一帧（页面滚动过才会，底色露出来像闪了一下）。模拟器里两步隔 45ms 仍空白、
 * 100ms 起不再空白；这里多留几倍给真机，到时抽屉也已升完。
 */
const RELEASE_DELAY_MS = 300;

/** iPhone、iPad（桌面版 Safari 自称 MacIntel，但有触点）。 */
export function isIOS(win: Window): boolean {
  const { platform, maxTouchPoints } = win.navigator;
  return (
    /iP(ad|hone|od)/.test(platform) ||
    (platform === "MacIntel" && maxTouchPoints > 1)
  );
}

/** 打开抽屉时让 body 独立滚动，避免拆装主题节点；关闭后恢复根页面位置。 */
export function createStorefrontScroll(doc: Document) {
  const win = doc.defaultView;
  const body = doc.body;
  if (!win || win.getComputedStyle(body).position === "fixed") return;
  const { scrollX, scrollY } = win;
  const restorers: (() => void)[] = [];
  /** 写上样式并登记还原；返回的函数用来改写自己写过的值。主题在抽屉打开期间主动改写的属性归主题所有，两边都不再碰。 */
  const set = (style: CSSStyleDeclaration, property: string, value: string) => {
    const previous = style.getPropertyValue(property);
    const priority = style.getPropertyPriority(property);
    let written = value;
    const ours = () =>
      style.getPropertyValue(property) === written &&
      !style.getPropertyPriority(property);
    style.setProperty(property, value);
    restorers.push(() => {
      if (ours()) style.setProperty(property, previous, priority);
    });
    return (next: string) => {
      if (!ours()) return;
      style.setProperty(property, next);
      written = next;
    };
  };
  set(doc.documentElement.style, "overflow", "hidden");
  set(body.style, "position", "fixed");
  set(body.style, "top", "0px");
  set(body.style, "right", "0px");
  set(body.style, "bottom", "0px");
  set(body.style, "left", "0px");
  set(body.style, "height", "auto");
  set(body.style, "min-height", "0px");
  set(body.style, "max-height", "none");
  // 先当成不能手动滚的裁剪框把位置摆好，放开时只改 overflow，不再是一次新的程序滚动。
  const setOverflow = set(body.style, "overflow", "hidden");
  body.scrollLeft = scrollX;
  body.scrollTop = scrollY;
  let locked = false;
  let released = false;
  let disposed = false;
  const sync = () => {
    setOverflow(released && !locked ? "auto" : "hidden");
  };
  const release = win.setTimeout(() => {
    released = true;
    sync();
  }, RELEASE_DELAY_MS);
  return {
    setLocked(next: boolean) {
      if (disposed || next === locked) return;
      locked = next;
      sync();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      win.clearTimeout(release);
      const left = body.scrollLeft;
      const top = Math.max(0, body.scrollTop);
      for (const restore of restorers.reverse()) restore();
      win.scrollTo({ left, top, behavior: "instant" });
    },
  };
}
